// iter 275 round 36 — Modal globale che appare quando il backend ritorna 402
// "insufficient_credits". Ascolta `pronto:credits-exhausted` window event
// dispatchato dall'interceptor di lib/api.js. Mostra messaggio + CTA verso
// /dashboard/crediti per top-up. Auto-chiude su navigate.
//
// iter v29 r270 — Aggiunto upgrade path contestuale: quando un utente sul
// piano "start" esaurisce i crediti, oltre al top-up gli proponiamo il
// passaggio a "pro" con il valore concreto ("+170 cr/mese"). Stesso
// schema free→start, pro→studio. Doppia CTA: top-up (one-shot) +
// upgrade piano (ricorrente, soldi veri).
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, X, Coins, ArrowUpRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { isReaderMode, READER_MODE_NOTICE } from "@/lib/iosReader";

const UPGRADE_PATH = {
  free:   { next: "start",  label: "Start",  monthlyCr: 150,  monthlyEur: 29 },
  beta:   { next: "start",  label: "Start",  monthlyCr: 150,  monthlyEur: 29 },
  trial:  { next: "start",  label: "Start",  monthlyCr: 150,  monthlyEur: 29 },
  start:  { next: "pro",    label: "Pro",    monthlyCr: 320,  monthlyEur: 49 },
  pro:    { next: "studio", label: "Agenzia", monthlyCr: 640,  monthlyEur: 99 },
  // pro_agency non ha upgrade
};

export default function CreditExhaustedModal() {
  const [data, setData] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [currentBucket, setCurrentBucket] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = async (e) => {
      setData(e.detail || {});
      // Carica piano corrente al volo per personalizzare CTA upgrade
      try {
        const r = await api.get("/credits/me");
        setCurrentPlan(r.data?.plan || r.data?.effective_plan || null);
        setCurrentBucket(Number(r.data?.bucket || 0));
      } catch { /* silenzioso, fall-back al solo top-up */ }
    };
    window.addEventListener("pronto:credits-exhausted", handler);
    return () => window.removeEventListener("pronto:credits-exhausted", handler);
  }, []);

  if (!data) return null;

  const detailMessage = data.message || data.detail || "";
  const isBetaDaily = /giornalieri della beta|rinnovano fra circa/i.test(detailMessage);
  const upgrade = currentPlan ? UPGRADE_PATH[String(currentPlan).toLowerCase()] : null;

  const close = () => setData(null);
  const goCrediti = () => { setData(null); navigate("/dashboard/crediti"); };
  const goPricing = () => { setData(null); navigate(`/pricing?upgrade=${upgrade?.next || "pro"}`); };

  const deltaCr = upgrade && currentBucket ? Math.max(0, upgrade.monthlyCr - currentBucket) : 0;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 py-6 animate-in fade-in duration-150"
      onClick={close}
      data-testid="credits-exhausted-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-in slide-in-from-bottom-4 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-[#F1F4ED] text-[#6B6A65] inline-flex items-center justify-center"
          aria-label="Chiudi"
          data-testid="credits-exhausted-close"
        >
          <X size={16} weight="bold" />
        </button>

        <div className="w-12 h-12 rounded-full bg-[#FFF8E6] inline-flex items-center justify-center text-[#C8A66B] mb-3">
          <Coins size={24} weight="duotone" />
        </div>

        <h2 className="font-editorial text-2xl text-[#1C1C1A]">
          {isBetaDaily ? "Per oggi è tutto" : "Crediti esauriti"}
        </h2>
        <p className="mt-2 text-[13px] text-[#6B6A65]">
          {detailMessage || "Hai finito i crediti del mese."}
          {!isBetaDaily && data.needed && data.available !== undefined && (
            <> Servivano <strong>{data.needed}</strong> crediti, ne hai <strong>{data.available}</strong>.</>
          )}
        </p>

        {/* Beta giornaliero — info attesa */}
        {isBetaDaily && (
          <p className="mt-3 text-[12.5px] text-[#6B6A65]">
            Pronto include tutte le funzioni:
            ti diamo un tot di crediti al giorno per provare. Torna domani:
            si rinnovano a mezzanotte.
          </p>
        )}

        {/* Upgrade pitch — solo se piano ha next disponibile.
            iter v30 r137 — Apple Reader-app compliance: su iOS native NON
            mostriamo prezzi o pitch upgrade. Sostituiamo con messaggio
            neutro che rinvia al sito (no anti-steering, link generico). */}
        {!isBetaDaily && upgrade && !isReaderMode && (
          <div className="mt-4 bg-[#F1F4ED] border border-[#D5DAC9] rounded-lg p-3.5" data-testid="credits-exhausted-upgrade-box">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#5A6A4D] font-semibold mb-1">
              Vuoi non pensarci più?
            </div>
            <div className="text-[13px] text-[#1C1C1A] leading-snug">
              Passa a <strong>{upgrade.label}</strong> e hai <strong>{upgrade.monthlyCr} crediti/mese</strong>
              {deltaCr > 0 && <> · <span className="text-[#7C8B6E]">+{deltaCr} in più</span></>}
              {" "}per <strong>{upgrade.monthlyEur}€/mese</strong>.
            </div>
          </div>
        )}

        {!isBetaDaily && isReaderMode && (
          <p className="mt-3 text-[12.5px] text-[#6B6A65] leading-snug">
            {READER_MODE_NOTICE}
          </p>
        )}

        {/* Standard top-up info — solo se nessun upgrade pitch sopra E non siamo in Reader mode */}
        {!isBetaDaily && !upgrade && !isReaderMode && (
          <p className="mt-3 text-[12.5px] text-[#6B6A65]">
            Compra un pacchetto di crediti aggiuntivi oppure aspetta il rinnovo automatico del prossimo mese.
          </p>
        )}

        {/* CTA buttons */}
        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          {isBetaDaily || isReaderMode ? (
            <button
              onClick={close}
              className="w-full py-2.5 rounded-md text-[13px] font-medium border border-[#EAE7DF] text-[#6B6A65] hover:bg-[#F1F4ED]"
              data-testid="credits-exhausted-dismiss"
            >
              Capito
            </button>
          ) : (
            <>
              <button
                onClick={goCrediti}
                className="flex-1 py-2.5 rounded-md text-[13px] font-medium border border-[#EAE7DF] text-[#1C1C1A] hover:bg-[#F1F4ED] inline-flex items-center justify-center gap-1.5"
                data-testid="credits-exhausted-go"
              >
                <CreditCard size={14} weight="bold" /> Crediti aggiuntivi
              </button>
              {upgrade && (
                <button
                  onClick={goPricing}
                  className="flex-1 py-2.5 rounded-md text-[13px] font-semibold bg-[#7C8B6E] text-white hover:bg-[#6E7C61] inline-flex items-center justify-center gap-1.5"
                  data-testid="credits-exhausted-upgrade"
                >
                  Passa a {upgrade.label} <ArrowUpRight size={14} weight="bold" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
